import { z } from 'zod';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { getBackendPluginExtensionsDir } from '@wix/cli-astro-definitions';
import { VALID_PATH_REGEX } from '../../../dev-center/components/validations/common.js';
import { createServicePluginValidation } from '../../../dev-center/components/validations/service-plugin.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { generateServicePlugin } from './generate.js';

const schema = z.object({
  extensionType: z.literal('SERVICE_PLUGIN'),
  pluginType: z
    .union([
      z
        .literal(ComponentType.ECOM_ADDITIONAL_FEES)
        .describe(defineField({ title: 'Ecom Additional Fees' })),
      z
        .literal(ComponentType.ECOM_SHIPPING_RATES)
        .describe(defineField({ title: 'Ecom Shipping Rates' })),
      z
        .literal(ComponentType.ECOM_DISCOUNTS_TRIGGER)
        .describe(defineField({ title: 'Ecom Discount Triggers' })),
      z
        .literal(ComponentType.ECOM_VALIDATIONS)
        .describe(defineField({ title: 'Ecom Validations' })),
      z
        .literal(ComponentType.ECOM_PAYMENT_SETTINGS)
        .describe(defineField({ title: 'Ecom Payment Settings' })),
      z
        .literal(ComponentType.GIFT_CARDS_PROVIDER)
        .describe(defineField({ title: 'Ecom Gift Cards Provider' })),
      z
        .literal(ComponentType.STAFF_SORTING_PROVIDER)
        .describe(defineField({ title: 'Staff Sorting Provider' })),
      z
        .literal(ComponentType.BOOKING_POLICY_PROVIDER)
        .describe(defineField({ title: 'Booking Policy Provider' })),
      z
        .literal(ComponentType.AVAILABILITY_TIME_SLOTS_CONFIGURATION_PROVIDER)
        .describe(
          defineField({
            title: 'Availability Time Slots Configuration Provider',
          })
        ),
      z
        .literal(ComponentType.BOOKINGS_VALIDATION_PROVIDER)
        .describe(defineField({ title: 'Bookings Validation Provider' })),
      z
        .literal(ComponentType.TOOLS_PROVIDER_CONFIG)
        .describe(defineField({ title: 'Tools Provider' })),
      z
        .literal(ComponentType.BOOKINGS_PRICING_PROVIDER)
        .describe(defineField({ title: 'Bookings Pricing Provider' })),
      z
        .literal(ComponentType.REALTIME_PERMISSIONS_PROVIDER)
        .describe(defineField({ title: 'Realtime Permissions Provider' })),
    ])
    .describe(defineField({ title: 'Plugin type' })),
  name: z
    .string()
    .regex(VALID_PATH_REGEX)
    .max(19)
    .describe(
      defineField({
        title: 'Plugin name',
        description: 'Lowercase alphanumeric and hyphens only.',
      })
    ),
});

export const servicePlugin: ExtensionDef<typeof schema> = {
  title: 'Service Plugin',
  description: 'A backend service plugin that extends a Wix service',
  group: 'Backend',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/backend-extensions/service-plugins/add-service-plugin-extensions',
  zodSchema: schema,
  validate: async (projectFolder, data) => {
    const result = createServicePluginValidation(
      getBackendPluginExtensionsDir(projectFolder)
    )(data.name);
    return result.valid ? null : result.reason;
  },
  generate: ({ projectFolder, data }) =>
    generateServicePlugin(projectFolder, data.pluginType, data.name),
};
